import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Box, Card, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { galleryService } from "../../api/service.ts";
import type { GalleryItem } from "../../api/types";
import { MatchDialog } from "../MatchDialog.tsx";
import PhotoLightbox from "./PhotoLightbox.tsx";

const AUTOPLAY_MS = 20000;
const MAX_DOTS = 12;
const SWIPE_PX = 40;

function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
}

interface Props {
    /** mostra il pulsante per aprire il dettaglio partita (no per gli ospiti) */
    showDetails?: boolean;
}

/**
 * Carosello di cartoline: foto di squadra, data, avversario e risultato delle partite giocate.
 * Clic sulla foto = schermo intero; swipe su touch; frecce da tastiera a schermo intero.
 */
/** Ordine casuale (Fisher-Yates): a ogni apertura la galleria parte da una foto diversa */
function shuffle<T>(list: T[]): T[] {
    const a = [...list];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export default function MatchGallery({ showDetails = true }: Props) {
    const [items, setItems] = useState<GalleryItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [openMatch, setOpenMatch] = useState<GalleryItem | null>(null);
    const [fullscreen, setFullscreen] = useState(false);
    const touchStartX = useRef<number | null>(null);

    useEffect(() => {
        galleryService.getGallery()
            .then((list) => setItems(shuffle(list)))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    const count = items.length;
    const go = useCallback((delta: number) => setIndex((i) => (i + delta + count) % count), [count]);

    // scorrimento automatico, in pausa con il mouse sopra o con una partita aperta
    useEffect(() => {
        if (count < 2 || paused || openMatch || fullscreen) return;
        const t = setInterval(() => go(1), AUTOPLAY_MS);
        return () => clearInterval(t);
    }, [count, paused, openMatch, fullscreen, go]);

    const swipeHandlers = {
        onTouchStart: (e: TouchEvent) => { touchStartX.current = e.touches[0].clientX; },
        onTouchEnd: (e: TouchEvent) => {
            if (touchStartX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            touchStartX.current = null;
            if (Math.abs(dx) > SWIPE_PX) go(dx < 0 ? 1 : -1);
        },
    };

    // precarica la foto successiva per una transizione senza attese
    useEffect(() => {
        if (count < 2) return;
        const next = items[(index + 1) % count];
        new Image().src = galleryService.teamPhotoUrl(next.matchId);
    }, [index, items, count]);

    if (loading) {
        return (
            <Card sx={{ borderRadius: 4, p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Card>
        );
    }

    if (count === 0) {
        return (
            <Card sx={{ borderRadius: 4, p: 4, textAlign: "center", color: "text.secondary" }}>
                <PhotoCameraIcon sx={{ fontSize: 48, opacity: 0.4 }} />
                <Typography>Nessuna foto di squadra ancora caricata</Typography>
            </Card>
        );
    }

    const item = items[Math.min(index, count - 1)];
    // pareggio (solo amichevoli) in grigio neutro
    const resultColor = item.outcome === "WIN" ? "success" : item.outcome === "LOSS" ? "error" : "default";

    return (
        <>
            <Card
                elevation={4}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                sx={{ borderRadius: 4, overflow: "hidden", bgcolor: "#fff", p: 1.5, pb: 1 }}
            >
                {/* foto, con cornice bianca da cartolina */}
                <Box
                    sx={{ position: "relative", aspectRatio: "3 / 2", bgcolor: "grey.200", borderRadius: 2, overflow: "hidden", cursor: "zoom-in" }}
                    onClick={() => setFullscreen(true)}
                    {...swipeHandlers}
                >
                    <Box
                        key={item.matchId}
                        component="img"
                        src={galleryService.teamPhotoUrl(item.matchId)}
                        alt={`Squadra ${item.date} vs ${item.opponent}`}
                        sx={{
                            width: "100%", height: "100%", objectFit: "cover", display: "block",
                            animation: "galleryFade 0.6s ease",
                            "@keyframes galleryFade": { from: { opacity: 0 }, to: { opacity: 1 } },
                        }}
                    />
                    {count > 1 && (
                        <>
                            <IconButton
                                aria-label="Foto precedente"
                                onClick={(e) => { e.stopPropagation(); go(-1); }}
                                sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.35)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.55)" } }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <IconButton
                                aria-label="Foto successiva"
                                onClick={(e) => { e.stopPropagation(); go(1); }}
                                sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.35)", color: "white", "&:hover": { bgcolor: "rgba(0,0,0,0.55)" } }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </>
                    )}
                    <Chip
                        label={item.seasonCode}
                        size="small"
                        sx={{ position: "absolute", top: 8, right: 8, bgcolor: "rgba(255,255,255,0.85)", fontWeight: 600 }}
                    />
                </Box>

                {/* didascalia */}
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 1.2, px: 0.5 }} gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            vs {item.opponent}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ textTransform: "capitalize" }}>
                            {formatDate(item.date)}
                        </Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                        {item.result && (
                            <Chip label={item.result} color={resultColor} sx={{ fontWeight: 700, fontSize: "1.05rem", px: 0.5 }} />
                        )}
                        {showDetails && (
                            <Tooltip title="Dettaglio partita">
                                <IconButton size="small" onClick={() => setOpenMatch(item)} aria-label="Dettaglio partita">
                                    <InfoOutlinedIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                {/* posizione nel carosello */}
                {count > 1 && (
                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.6} sx={{ mt: 1 }}>
                        {count <= MAX_DOTS ? (
                            items.map((it, i) => (
                                <Box
                                    key={it.matchId}
                                    onClick={() => setIndex(i)}
                                    sx={{
                                        width: i === index ? 18 : 8, height: 8, borderRadius: 4, cursor: "pointer",
                                        bgcolor: i === index ? "primary.main" : "grey.400", transition: "all 0.3s",
                                    }}
                                />
                            ))
                        ) : (
                            <Typography variant="caption" color="text.secondary">{index + 1} / {count}</Typography>
                        )}
                    </Stack>
                )}
            </Card>

            <PhotoLightbox
                open={fullscreen}
                photos={items.map((it) => ({
                    src: galleryService.teamPhotoUrl(it.matchId),
                    alt: `Squadra ${it.date} vs ${it.opponent}`,
                    title: `vs ${it.opponent}${it.result ? ` · ${it.result}` : ""}`,
                    subtitle: `${formatDate(it.date)} · ${it.seasonCode}`,
                }))}
                index={index}
                onIndexChange={setIndex}
                onClose={() => setFullscreen(false)}
            />

            {openMatch && (
                <MatchDialog
                    open
                    onClose={() => setOpenMatch(null)}
                    matchId={openMatch.matchId}
                    eventId={openMatch.eventId}
                />
            )}
        </>
    );
}
